import { FilterChip } from '@silverfort/sf-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ResourcePicker from './ResourcePicker';
import { SEARCH_MIN_CHARS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../utils';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { Entity } from '../../../api';

type ResourcePickerFilterProps = {
    filterTitle: string;
    chosenIps?: string[];
    chosenResources?: Entity[];
    onApplyFilter: (ips: string[], resources: Entity[]) => void;
    onRemoveFilter: () => void;
};

enum ResourceType {
    RESOURCES = 'resources',
    IPS = 'ips'
}

const ResourcePickerFilter = ({
    filterTitle,
    chosenIps,
    chosenResources,
    onApplyFilter,
    onRemoveFilter
}: ResourcePickerFilterProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [loadingResources, setLoadingResources] = useState(false);
    const [selectedResources, setSelectedResources] = useState<Entity[]>([]);
    const [selectedIPs, setSelectedIPs] = useState<string[]>([]);

    const [ipSearchTerm, setIpSearchTerm] = useState('');
    const [resourceSearchTerm, setResourceSearchTerm] = useState('');
    const [matchedIps, setMatchedIps] = useState<string[]>([]);
    const [matchedResources, setMatchedResources] = useState<Entity[]>([]);

    useEffect(() => {
        setSelectedIPs(chosenIps || []);
    }, [chosenIps]);

    useEffect(() => {
        setSelectedResources(chosenResources || []);
    }, [chosenResources]);

    const searchIPs = useCallback(() => {
        setMatchedIps(ipSearchTerm ? selectedIPs.filter((ip) => ip.startsWith(ipSearchTerm)) : selectedIPs);
    }, [ipSearchTerm, selectedIPs]);

    useEffect(() => {
        searchIPs();
    }, [searchIPs]);

    const searchResources = useCallback(async () => {
        try {
            let escapedQuery = resourceSearchTerm?.trim() || '';

            if (escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) {
                setMatchedResources(selectedResources);
            } else {
                setLoadingResources(true);
                escapedQuery = encodeURIComponent(escapedQuery);
                const baseSearchURL = '/users/searchEntitiesByTypes';
                const reqUrl = `${baseSearchURL}/${escapedQuery}/${1}`;

                const res: { data: { Results: Entity[] } | null } = await httpGet(reqUrl, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: { hideDeleted: true, entitiesTypes: [AD_ENTITY_TYPES.SERVER] }
                    }
                });

                const result = res.data?.Results || [];
                setMatchedResources(result);
            }
        } catch (e) {
            console.error('Failed to search resources for query ' + resourceSearchTerm, e);
        } finally {
            setLoadingResources(false);
        }
    }, [resourceSearchTerm, selectedResources]);

    useEffect(() => {
        searchResources();
    }, [resourceSearchTerm]);

    const onSearchChange = useCallback((val: string, type: ResourceType) => {
        if (type === ResourceType.IPS) {
            setIpSearchTerm(val);
        } else {
            setResourceSearchTerm(val);
        }
    }, []);

    const onRemoveResource = useCallback((item: Entity | string, type: ResourceType) => {
        if (type === ResourceType.IPS) {
            setSelectedIPs((prev) => prev.filter((ip) => ip !== (item as string)));
        } else {
            setSelectedResources((prev) => prev.filter((res) => res.guid !== (item as Entity).guid));
        }
    }, []);

    const onSelect = useCallback((item: Entity | string, type: ResourceType) => {
        if (type === ResourceType.IPS) {
            setSelectedIPs((prev) => {
                if (!prev.includes(item as string)) {
                    return [...prev, item as string];
                }
                return prev;
            });
        } else {
            setSelectedResources((prev) => {
                if (!prev.find((res) => res.guid === (item as Entity).guid)) {
                    return [...prev, item as Entity];
                }
                return prev;
            });
        }
    }, []);

    const onClose = () => {
        setMenuOpen(false);
        setIpSearchTerm('');
        setResourceSearchTerm('');
    };

    const onAction = () => {
        onClose();
        onApplyFilter(selectedIPs, selectedResources);
    };

    const onRemove = () => {
        setSelectedIPs([]);
        setSelectedResources([]);
        setMatchedIps([]);
        setMatchedResources([]);
        onRemoveFilter();
    };

    const filterValueText = useMemo(() => {
        let info = '';
        if ((chosenIps?.length || 0) + (chosenResources?.length || 0) > 1) {
            info += ` (${(chosenIps?.length || 0) + (chosenResources?.length || 0)}) Selected`;
        } else if (chosenIps?.length) {
            info += `${chosenIps[0]}`;
        } else if (chosenResources?.length) {
            info += `${chosenResources[0].name}`;
        }
        return info;
    }, [chosenIps, chosenResources]);

    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div>
            <FilterChip
                text={
                    <span className="flex gap-1">
                        <span className="flex gap-0.5">
                            <span>{`${filterTitle ?? 'Filter'}`}</span>:
                        </span>
                        {!!filterValueText ? (
                            <span className="font-medium">{`${filterValueText}`}</span>
                        ) : (
                            <span>All</span>
                        )}
                    </span>
                }
                selected={menuOpen || !!filterValueText}
                onClick={(e) => {
                    setMenuOpen(true);
                }}
                removable={(chosenIps?.length || 0) + (chosenResources?.length || 0) > 0}
                onRemove={onRemove}
            />

            {menuOpen && (
                <div ref={menuRef} className="relative">
                    <div className="w-full z-10 absolute top-2">
                        <ResourcePicker
                            title={filterTitle}
                            // @ts-ignore - we add this because the ResourcePicker component is not typed
                            resources={matchedResources}
                            // @ts-ignore
                            ips={matchedIps}
                            // @ts-ignore
                            selectedIps={selectedIPs}
                            // @ts-ignore
                            selectedResources={selectedResources}
                            showAdTypeFilter={false}
                            showAddAll={false}
                            loading={loadingResources}
                            onSearchChange={onSearchChange}
                            onSelect={onSelect}
                            onClose={onClose}
                            onAction={onAction}
                            // @ts-ignore
                            onRemove={onRemoveResource}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourcePickerFilter;
